import React, { useEffect, useMemo, useState } from 'react';
import { Tag, ListChecks, Layers, Car, CircleDollarSign, CalendarClock } from 'lucide-react';
import { fetchProductRatesAnalytics, formatCurrency } from '../services/adminAnalyticsApi';
import '../styles/analytics-theme.css';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}

function CoverTypeBadge({ value }) {
  const n = String(value || '').toUpperCase();
  if (n === 'TPO') return <span className="ab-badge sky">{n}</span>;
  if (n === 'TOR') return <span className="ab-badge violet">{n}</span>;
  return <span className="ab-badge muted">{n || '—'}</span>;
}

function AccentStatCard({ title, value, subtitle, icon: Icon, accent }) {
  return (
    <div className={`ab-accent-card ${accent}`}>
      <div className="ab-accent-card-inner">
        <div className={`ab-accent-icon ${accent}`}>
          <Icon size={24} />
        </div>
        <div>
          <p className="ab-stat-label">{title}</p>
          <p className="ab-stat-value">{value}</p>
          {subtitle && <p className="ab-stat-subtitle">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

const ProductRatesPage = ({ onShowNotification }) => {
  const [coverType, setCoverType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [rates, setRates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setFetching(true);
      try {
        const result = await fetchProductRatesAnalytics({ coverType });
        if (!cancelled) setRates(result.rates || []);
      } catch (error) {
        if (!cancelled && onShowNotification) onShowNotification(error.message, 'error');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetching(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverType]);

  const stats = useMemo(() => {
    const active = rates.filter((r) => r.is_active);
    const coverTypes = new Set(rates.map((r) => r.cover_type).filter(Boolean));
    const vehicleClasses = new Set(rates.map((r) => r.vehicle_class).filter(Boolean));
    const avg = active.length
      ? active.reduce((s, r) => s + (Number(r.gross_amount ?? r.net_amount ?? r.base_amount) || 0), 0) / active.length
      : 0;
    const lastUpdated = rates.reduce((max, r) => {
      const t = r.updated_at ? new Date(r.updated_at).getTime() : 0;
      return t > max ? t : max;
    }, 0);
    return {
      totalRates: rates.length,
      activeRates: active.length,
      coverTypes: coverTypes.size,
      vehicleClasses: vehicleClasses.size,
      averageRate: avg,
      lastUpdated: lastUpdated ? new Date(lastUpdated).toISOString() : null,
    };
  }, [rates]);

  const coverTypeOptions = useMemo(
    () => [...new Set(rates.map((r) => r.cover_type).filter(Boolean))].sort(),
    [rates]
  );

  const filtered = coverType === 'all'
    ? rates
    : rates.filter((r) => r.cover_type === coverType);

  if (loading) {
    return <div className="ab-page"><div className="ab-empty">Loading product rates…</div></div>;
  }

  return (
    <div className="ab-page">
      <div className="ab-page-header">
        <div className="ab-header-with-icon">
          <div className="ab-page-icon-wrap"><Tag size={20} /></div>
          <div>
            <h1 className="ab-page-title">Product Rates</h1>
            <p className="ab-page-subtitle">Manage pricing rates for TPO and TOR covers</p>
          </div>
        </div>
        {fetching && <span className="ab-refresh-badge">Refreshing data…</span>}
      </div>

      <div className="ab-stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <AccentStatCard title="Active Rates" value={stats.activeRates.toLocaleString()} subtitle={`of ${stats.totalRates.toLocaleString()} total`} icon={ListChecks} accent="emerald" />
        <AccentStatCard title="Cover Types" value={stats.coverTypes.toLocaleString()} subtitle="TPO and TOR covers" icon={Layers} accent="violet" />
        <AccentStatCard title="Vehicle Classes" value={stats.vehicleClasses.toLocaleString()} subtitle="distinct classes priced" icon={Car} accent="amber" />
        <AccentStatCard title="Average Rate" value={formatCurrency(stats.averageRate)} subtitle="across active rates" icon={CircleDollarSign} accent="sky" />
      </div>

      <div className="ab-card" style={{ marginBottom: '1.5rem' }}>
        <div className="ab-card-body">
          <div className="ab-filter-bar">
            <div className="ab-filter-item">
              <label>Filter by Cover Type</label>
              <select value={coverType} onChange={(e) => setCoverType(e.target.value)}>
                <option value="all">All Cover Types</option>
                {coverTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {stats.lastUpdated && (
              <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
                <span className="ab-badge muted" style={{ display: 'inline-flex', gap: '0.375rem', padding: '0.5rem 0.75rem' }}>
                  <CalendarClock size={14} />
                  Last updated {formatDate(stats.lastUpdated)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="ab-card">
        <div className="ab-card-header">
          <h3 className="ab-card-title">Rate Schedule</h3>
        </div>
        <div className="ab-card-body-flush">
          <div className="ab-table-wrap">
            <table className="ab-table" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Cover Type</th>
                  <th>Vehicle Class</th>
                  <th>Period</th>
                  <th className="highlight-primary" style={{ textAlign: 'right' }}>Net (Basic)</th>
                  <th className="highlight-emerald" style={{ textAlign: 'right' }}>Gross (Customer Pays)</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id} style={{ opacity: row.is_active ? 1 : 0.6 }}>
                    <td><CoverTypeBadge value={row.cover_type} /></td>
                    <td style={{ fontWeight: 500 }}>{row.vehicle_class || '—'}</td>
                    <td style={{ color: 'var(--ab-muted-fg)' }}>{row.period || '—'}</td>
                    <td className="cell-primary" style={{ textAlign: 'right' }}>{formatCurrency(row.net_amount ?? row.base_amount)}</td>
                    <td className="cell-emerald" style={{ textAlign: 'right' }}>{formatCurrency(row.gross_amount)}</td>
                    <td>
                      <span className={`ab-badge ${row.is_active ? 'success' : 'muted'}`}>
                        {row.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--ab-muted-fg)', fontSize: '0.75rem' }}>{formatDate(row.updated_at)}</td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={7} className="ab-empty">No product rates match the current filter.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductRatesPage;
